import React, { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { debounce } from 'lodash';
import { Form, FormInstance, TextInput } from 'blueprint-form';
import { faSignInAlt } from '@fortawesome/pro-solid-svg-icons';

import Button from '../../molecules/button';
import Card from '../../organisms/card';
import Callout from '../../organisms/callout';
import Icon from '../../atoms/icon';
import Meta from '../../atoms/meta';
import { useAuth } from '../../../contexts/auth';
import { useHasSSO, LoginUser } from '../../../libs/user';
import { IsInIframe } from '../../../libs/browser';

interface LoginPageProps {
  onSubmitHover: () => void;
}

interface LoginData {
  email: string;
  password: string;
}

const REGEX_PATIENT_URL = /^\/patient\/\d+/;

const LoginPage: React.FC<LoginPageProps> = ({ onSubmitHover }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>(null);
  const [domain, setDomain] = useState('');
  const [ssoEmail, setSSOEmail] = useState('');
  const [disableSSODomain, setDisableSSODomain] = useState('');

  const { updateUser } = useAuth();
  const { hasSSO, isBreakGlass } = useHasSSO(domain, ssoEmail);

  const location = useLocation();

  useEffect(() => {
    if (IsInIframe() && REGEX_PATIENT_URL.test(location.pathname)) {
      navigate('/patient_notfound');
    }
  }, []);

  const initialData: LoginData = {
    email: '',
    password: ''
  };

  const form = new FormInstance<LoginData>(initialData);

  const onSubmit = (data: LoginData) => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);
    LoginUser({
      email: data.email,
      password: data.password
    }).then((response) => {
      setIsLoading(false);
      if (response.status !== 'OK') {
        setError(response.message);
        return;
      }
      updateUser(response.user);
    });
  };

  const updateDomain = useCallback(
    debounce((email: string) => {
      const parts = email.split('@');
      if (parts?.length === 2 && parts[1]) {
        setSSOEmail(email);
        if (domain !== parts[1]) {
          setDomain(parts[1]);
          setDisableSSODomain('');
        }
      }
    }, 1000),
    [domain]
  );

  const onChange = (data: LoginData) => {
    updateDomain(data.email);
  };

  const onSSO = () => {
    window.location.href = `/user/sso/?realm=${domain}`;
  };

  const isSSO = hasSSO && disableSSODomain !== domain;

  return (
    <Card className="mb-8">
      <Meta title="Login" />
      <h1 className="text-sans-lg mb-7">
        <Icon icon={faSignInAlt} /> Log in to your account
      </h1>
      <Form form={form} onSubmit={onSubmit} onChange={onChange}>
        <TextInput
          autoComplete="email"
          label="Email"
          name="email"
          type="email"
          required
        />
        {!isSSO && (
          <TextInput
            autoComplete="password"
            label="Password"
            name="password"
            type="password"
            required
          />
        )}
        {error && !isSSO && (
          <Callout intent="danger" className="mb-8">
            {error}
          </Callout>
        )}
        <p className="mb-8 mt-4">
          {isSSO ? (
            isBreakGlass ? (
              <Button
                bare
                variant="minimal"
                onClick={() => setDisableSSODomain(domain)}
              >
                <span className="text-brand hover-brand-text">
                  Login using email & password instead
                </span>
              </Button>
            ) : null
          ) : (
            <Link className="text-brand hover-brand-text" to="/reset/">
              Forgot password? Request reset here.
            </Link>
          )}
        </p>
        {isSSO ? (
          <Button intent="success" disabled={isLoading} fill onClick={onSSO}>
            Single Sign On
          </Button>
        ) : (
          <Button
            type="submit"
            intent="success"
            disabled={isLoading}
            onMouseOver={onSubmitHover}
            onFocus={onSubmitHover}
            fill
          >
            Log In
          </Button>
        )}
      </Form>
    </Card>
  );
};

export default LoginPage;
